import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';
import type { FileAccessError } from '@/types/types';
import { OpenOptions } from '@/config/openOptions';
import { captureErrorForSentry } from '@/components/utils';
import { REQUIRED_PHONE_VERIFICATION } from '@/consts';
import parseRecipientEmail from '@/components/MiniApps/RecipientPage/EmailRendering/parseRecipientEmail';
import type { LetterContent } from '@/types/recipientEmail';

import type AuthSettingsStore from '../../AuthSettingsStore';
import type FilesAccessStore from '../../FilesAccessStore';
import type { AccessTypeState } from '../../FilesAccessStore';
import type { SharedFilesStore } from '../SharedFilesStore';

export type { LetterContent } from '@/types/recipientEmail';

class EmailMessageStore {
    constructor(
        authSettingsStore: AuthSettingsStore,
        filesAccessStore: FilesAccessStore,
        sharedFilesStore: SharedFilesStore,
    ) {
        this.filesAccessStore = filesAccessStore;
        this.sharedFilesStore = sharedFilesStore;
        this.authSettingsStore = authSettingsStore;
        makeObservable(this);
    }

    private readonly authSettingsStore: AuthSettingsStore;

    private readonly filesAccessStore: FilesAccessStore;

    private readonly sharedFilesStore: SharedFilesStore;

    private requestSequence = 0;

    @observable
    isLoading = false;

    @observable
    private isDownloadFailed = false;

    @observable
    letterContent: LetterContent | null = null;

    @computed
    get errorType(): FileAccessError | undefined {
        return (
            this.isDownloadFailed
                ? 'serverError'
                : this.messageDownloadState?.errorType
        );
    }

    @computed
    get messageId(): string | undefined {
        return this.sharedFilesStore.linkData?.message_id;
    }

    @computed
    private get messageDownloadState(): AccessTypeState<OpenOptions.download> | undefined {
        return this.filesAccessStore.getAccessTypeState(this.messageId, OpenOptions.download);
    }

    @action
    private setIsLoading(value: boolean): void {
        this.isLoading = value;
    }

    @action
    private setIsDownloadFailed(value: boolean): void {
        this.isDownloadFailed = value;
    }

    @action
    private setMessageContent(content: LetterContent | null): void {
        this.letterContent = content;
    }

    tryFetchMessage = async ({
        code, throwError = false,
    }: { code?: string, throwError?: boolean }): Promise<void> => {
        const requestedMessageId = this.messageId;
        this.requestSequence += 1;
        const sequence = this.requestSequence;
        runInAction(() => {
            this.isLoading = true;
            this.isDownloadFailed = false;
            this.letterContent = null;
        });
        try {
            this.filesAccessStore.setSelectedAccessFileId(requestedMessageId);
            await this.filesAccessStore.tryFetchAccessLink({
                fileId: requestedMessageId,
                code,
                accessType: OpenOptions.download,
                throwError: true,
                alwaysCheckMFA: !!this.sharedFilesStore.allFilesPoliciesAggregate?.[REQUIRED_PHONE_VERIFICATION],
            });
            const state = this.filesAccessStore.getAccessTypeState(requestedMessageId, OpenOptions.download);
            await this.processAccessLink(state, requestedMessageId, sequence);
        } catch (error) {
            if (sequence !== this.requestSequence || this.messageId !== requestedMessageId) return;
            captureErrorForSentry(error, 'EmailMessageStore.tryFetchMessage');
            if (!this.messageDownloadState?.errorType) {
                this.setIsDownloadFailed(true);
            }
            if (throwError) {
                throw error;
            }
        } finally {
            if (sequence === this.requestSequence) this.setIsLoading(false);
        }
    };

    private async processAccessLink(
        state: AccessTypeState<OpenOptions.download>,
        messageId: string,
        sequence: number,
    ): Promise<void> {
        const { result: { link } } = state;
        const fetchResponse = await fetch(link);
        if (!fetchResponse.ok) throw new Error(`Message download failed (${fetchResponse.status})`);
        const bytes = new Uint8Array(await fetchResponse.arrayBuffer());
        const content = parseRecipientEmail(bytes);
        if (sequence === this.requestSequence && this.messageId === messageId) this.setMessageContent(content);
    }
}

export default EmailMessageStore;
